/**
 * When this tab has to re-read the conversation from sphere-mcp, and what changed when it does.
 *
 * sphere-mcp owns the transcript, and two things change it with no stream open to this tab: a
 * background task settling (its result is written onto the tool call that started it) and the
 * report turn that follows, and a turn still running after a reload. Both leave a marker in the
 * stored messages, so the rule is a read of the transcript and nothing else:
 *  - a tool part whose output carries `structuredContent.sphereTask.status === "working"`;
 *  - an assistant message whose metadata says `custom.running === true` — the placeholder a turn
 *    stores when it starts, replaced by the answer when it ends.
 *
 * The age cutoff for a placeholder whose turn died belongs to sphere-mcp: its conversation read
 * reports one older than the longest plausible turn with `running: false`, on its own clock. This
 * file trusts the flag and keeps no timer, so a browser clock that disagrees with the server's
 * never decides it. The flag's shape is a cross-repo contract kept in sync by hand.
 */
import { type UIMessage, getToolName, isToolUIPart } from "ai";

/** How often a busy conversation is re-read. */
export const SYNC_INTERVAL_MS = 5_000;

type SphereTaskOutput = { structuredContent?: { sphereTask?: { status?: unknown } } };

/** Whether a tool call's output is a background task's interim ack, not its result. */
export function isPendingTaskOutput(output: unknown): boolean {
	return (output as SphereTaskOutput | undefined)?.structuredContent?.sphereTask?.status === "working";
}

/** Whether a tool part is a background task that has not settled yet. */
export function isPendingTaskPart(part: UIMessage["parts"][number]): boolean {
	return isToolUIPart(part) && part.state === "output-available" && isPendingTaskOutput(part.output);
}

function isRunningPlaceholder(message: UIMessage): boolean {
	const custom = (message.metadata as { custom?: { running?: unknown } } | undefined)?.custom;
	return custom?.running === true;
}

/** Whether a turn is running on the server: its placeholder is stored and not yet replaced. */
export function turnIsRunning(messages: readonly UIMessage[]): boolean {
	return messages.some(isRunningPlaceholder);
}

function isContentPart(part: UIMessage["parts"][number]): boolean {
	if (part.type === "step-start") return false;
	if (part.type === "text" || part.type === "reasoning") return part.text.trim() !== "";
	return true;
}

/**
 * Where the turn this tab sent stands in the stored conversation, read after its stream broke:
 *  - `"running"`: its user message is stored and followed by a turn still running on the server;
 *  - `"answered"`: its user message is stored and followed by an assistant message with content —
 *    non-blank text or reasoning, or any other part but a step boundary, the rule sphere-mcp's
 *    `hasKeepableContent` uses to decide a turn is worth storing;
 *  - `null`: anything else — the message never got stored (the request was turned away), or the
 *    turn is over with nothing after it but an empty placeholder (its process died mid-turn).
 */
export function ownTurnState(
	messages: readonly UIMessage[],
	ownUserMessageId: string | undefined,
): "running" | "answered" | null {
	const ownIndex = messages.findIndex((message) => message.id === ownUserMessageId);
	if (ownIndex < 0) return null;
	const after = messages.slice(ownIndex + 1);
	if (turnIsRunning(after)) return "running";
	const answered = after.some((message) => message.role === "assistant" && message.parts.some(isContentPart));
	return answered ? "answered" : null;
}

/** Whether the stored conversation is still changing on the server. */
export function conversationIsBusy(messages: readonly UIMessage[]): boolean {
	return messages.some(
		(message) => isRunningPlaceholder(message) || message.parts.some((part) => isPendingTaskPart(part)),
	);
}

/**
 * What the thread renders of a stored conversation: everything but assistant messages with no
 * parts — a running turn's placeholder, or one a report that never ran left behind. Busy-ness is
 * read off the stored messages BEFORE this, since the placeholder is the running marker.
 */
export function visibleMessages(messages: UIMessage[]): UIMessage[] {
	return messages.filter((message) => message.role !== "assistant" || message.parts.length > 0);
}

/** The tool names whose task was pending in `before` and has SUCCEEDED in `after` — a failed one changed nothing. */
export function settledTaskTools(before: readonly UIMessage[], after: readonly UIMessage[]): string[] {
	const pending = new Set<string>();
	for (const message of before) {
		for (const part of message.parts) if (isPendingTaskPart(part) && isToolUIPart(part)) pending.add(part.toolCallId);
	}
	const settled: string[] = [];
	for (const message of after) {
		for (const part of message.parts) {
			if (
				isToolUIPart(part) &&
				pending.has(part.toolCallId) &&
				part.state === "output-available" &&
				!isPendingTaskPart(part)
			) {
				settled.push(getToolName(part));
			}
		}
	}
	return settled;
}
